/**
 * @author       yontaek@zumper.com (Yon Taek Chung)
 * @copyright    Copyright (c) 2023, Zumper
 * @description  PinMarkerContainer
 */

import { selectIsMobile } from '@zcode-ui/web-zumper-global-state'
import { isWebGLError, trackGAEvent } from '@zcode/nr-utils'
import { hocCompose, withErrorBoundary } from '@zcode/react-utils'
import type { Listable, Pin, RawPinInventorySearch, State } from '@zcode/state-type'
import { connect } from 'react-redux'
import type { Dispatch } from 'redux'
import { ANALYTIC_CHANNEL, EVENT_OPENED, PIN_MARKER } from '../../../../constants/marker'
import { getCheckVisited, getHoveredListable } from '../../../../inspectors'
import { logger } from '../../../../logger'
import { setVisitedEntity } from '../../../../module/clusters/actions'
import { setHoveredClusterItem } from '../../../../module/searchUi/actions'
import { trackPinClickEvent } from '../../../../module/traktor/thunks/trackPinClickEvent'
import type { MixpanelMaplistClickType } from '../../../../types'
import { PinMarkerGroup, type PinMarkerGroupProps } from './PinMarkerGroup'

const errorCallback = (error: Error, info: string | null | undefined, forwardProps: unknown) => {
  if (!isWebGLError(error)) {
    logger.error('ErrorBoundary: caught by PinMarkerGroupContainer', info, forwardProps, error)
  }
}

interface OwnPropsType {
  analyticChannel?: string
  lat?: number
  listables: ReadonlyArray<{
    pin: Listable | Pin<RawPinInventorySearch>
    showPriceMarker: boolean
  }>
  lng?: number
  mixpanelMaplistClick: ({
    buttonClicked,
    entity,
    pageName,
    section,
  }: MixpanelMaplistClickType) => void
}

const mapStateToProps = (state: State, ownProps: OwnPropsType) => {
  const { listables } = ownProps
  const { paUrl } = listables?.[0].pin ?? {}

  const minPriceListable = listables.reduce((min, listable) => {
    if (listable.pin.minPrice && min.minPrice && listable.pin.minPrice < min.minPrice) {
      return listable.pin
    }
    return min
  }, listables[0].pin)

  const checkVisited = getCheckVisited(state)

  const hoveredEntity = getHoveredListable(state)

  let isHovered = false
  if (hoveredEntity?.paUrl) {
    isHovered = hoveredEntity.paUrl === paUrl
  }
  const isVisited = checkVisited({ paUrl })

  return {
    isDot: !listables?.[0]?.showPriceMarker,
    isHovered,
    isVisited,
    isMobile: selectIsMobile(state),
    listables,
    priceTxt: minPriceListable.priceTxt + '+',
  } satisfies Partial<PinMarkerGroupProps>
}

const mapDispatchToProps = (dispatch: Dispatch, ownProps: OwnPropsType) => {
  const { analyticChannel, mixpanelMaplistClick, listables } = ownProps

  return {
    onClick: () => {
      if (!listables?.length) {
        return
      }
      const { buildingId, listingId, lat, lng } = listables[0].pin
      trackGAEvent(analyticChannel || ANALYTIC_CHANNEL, EVENT_OPENED)
      mixpanelMaplistClick({
        buttonClicked: 'Property Pin',
        entity: listables[0].pin,
      })
      dispatch(
        trackPinClickEvent({
          buildingId,
          listingId,
          lat,
          lng,
          markerType: PIN_MARKER,
        })
      )
      dispatch(setVisitedEntity(listables[0].pin, true))
    },
    onMouseOver: () => {
      dispatch(setHoveredClusterItem(listables[0]))
    },
    onMouseOut: () => {
      dispatch(setHoveredClusterItem(null))
    },
  } satisfies Partial<PinMarkerGroupProps>
}

export const PinMarkerGroupContainer = hocCompose(
  connect(mapStateToProps, mapDispatchToProps),
  withErrorBoundary({ displayName: 'PinMarkerGroupContainer', errorCallback }),
  PinMarkerGroup
)
